import {
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type JSX,
  type ReactElement,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

import { cx } from './internal/cx.js';

export interface TooltipProps {
  /** Tooltip text shown on hover and focus. */
  content: string;
  children: ReactNode;
  className?: string;
}

function mergeDescribedBy(existing: string | undefined, tooltipId: string): string {
  return [existing, tooltipId].filter(Boolean).join(' ');
}

/** Hover/focus tooltip for icon-only controls and truncated labels. */
export function Tooltip({ content, children, className }: TooltipProps): JSX.Element {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const tooltipId = useId();
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });

  const updatePosition = useCallback((): void => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    // Prefer the wrapped control (button/icon) so a stretched wrapper in a
    // flex column does not anchor the tooltip to the full row width.
    const anchor =
      trigger.firstElementChild instanceof HTMLElement ? trigger.firstElementChild : trigger;
    const rect = anchor.getBoundingClientRect();
    setCoords({
      top: rect.top - 8,
      left: rect.left + rect.width / 2,
    });
  }, []);

  useEffect(() => {
    if (!visible) return;
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [visible, updatePosition]);

  useEffect(() => {
    if (!visible) return;

    function handleKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        event.preventDefault();
        setVisible(false);
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [visible]);

  function show(): void {
    updatePosition();
    setVisible(true);
  }

  function hide(): void {
    setVisible(false);
  }

  const describedBy = visible ? tooltipId : undefined;

  const child =
    isValidElement(children) && describedBy
      ? cloneElement(children as ReactElement<{ 'aria-describedby'?: string }>, {
          'aria-describedby': mergeDescribedBy(
            (children as ReactElement<{ 'aria-describedby'?: string }>).props['aria-describedby'],
            tooltipId,
          ),
        })
      : children;

  return (
    <span
      ref={triggerRef}
      className={cx('nrx-tooltip', className)}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {child}
      {visible && typeof document !== 'undefined'
        ? createPortal(
            <span
              id={tooltipId}
              role="tooltip"
              className="nrx-tooltip__content nrx-tooltip__content--portaled"
              style={{
                top: coords.top,
                left: coords.left,
                transform: 'translate(-50%, -100%)',
              }}
            >
              {content}
            </span>,
            document.body,
          )
        : null}
    </span>
  );
}
